/**
 * Anzeige-Regeln für die Maßnahmen im Mitarbeiterfenster (F28, Nacharbeit nach der
 * Abschlussprüfung).
 *
 * Zwei Dinge blieben still, und eines davon sagte etwas Falsches:
 *
 * - **Lesen.** Scheiterte die Leseanfrage, war die Antwort leer, und das Fenster zeigte
 *   „Noch keine Maßnahmen dokumentiert“. Eine abgelehnte oder fehlgeschlagene Anfrage ließ sich damit
 *   nicht von „es gibt wirklich keine“ unterscheiden. Für eine Führungskraft sah es aus, als wäre ihre
 *   eigene Dokumentation verschwunden. Der Satz erscheint jetzt nur noch nach einer erfolgreichen
 *   Antwort, die nichts geliefert hat. Solange keine Antwort da ist, steht „wird geladen“, nach einem
 *   Fehler ein Hinweis.
 * - **Anlegen.** Schlug es fehl, blieb das Formular mit dem getippten Text offen und der Knopf sprang
 *   auf „Speichern“ zurück, ohne ein Wort. Seit der Prüfung der Berichtslinie gibt es dafür zwei neue
 *   Wege: 403 (Person außerhalb der Linie) und 503 (ohne Personio lässt sich die Linie nicht prüfen).
 *
 * Alle Regeln und Sätze stehen hier und nicht im Fenster, weil nur diese Datei ohne Oberfläche
 * getestet wird (das Gate für die Unit-Tests läuft vor der Installation der UI-Pakete). Das Fenster
 * (`components/EmployeeDetailDrawer.tsx`) ordnet nur an. Die Datei bleibt deshalb ohne React und ohne
 * Importe (dieselbe Trennung wie `lib/probezeit-anzeige.ts`).
 */

/** Ausgang einer Abfrage, so wie React Query ihn als `status` meldet. */
export type AbfrageStatus = "pending" | "error" | "success";

/** Was die Maßnahmen-Liste zeigt: die Liste, einen Satz oder den Hinweis auf einen Fehler. */
export type MassnahmenListenZustand = "laedt" | "fehler" | "leer" | "liste";

/**
 * Entscheidet, was die Liste zeigt. `anzahl` ist die Zahl der gelieferten Maßnahmen.
 *
 * „Leer“ gibt es nur für eine erfolgreiche Antwort ohne Maßnahmen. Bei einem Fehler zählt der
 * Fehler, auch wenn React Query noch die Daten des letzten Erfolgs hält: Scheitert ein erneutes Laden
 * (nach dem Anlegen oder Ändern), bleiben die alten Daten erhalten und der Status ist trotzdem
 * „error“. Eine Liste, deren Stand nicht mehr bestätigt ist, wird nicht als aktuell gezeigt.
 */
export function massnahmenListenZustand(abfrage: {
  status: AbfrageStatus;
  anzahl: number;
}): MassnahmenListenZustand {
  switch (abfrage.status) {
    case "pending":
      return "laedt";
    case "error":
      return "fehler";
    case "success":
      return abfrage.anzahl > 0 ? "liste" : "leer";
  }
}

/** Der Satz, wenn die Liste noch lädt oder (nach einer erfolgreichen Antwort) leer ist. */
export function massnahmenHinweisText(zustand: "laedt" | "leer"): string {
  switch (zustand) {
    case "laedt":
      return "Maßnahmen werden geladen…";
    case "leer":
      return "Noch keine Maßnahmen dokumentiert";
  }
}

/** Was gescheitert ist: das Lesen der Liste oder das Anlegen einer neuen Maßnahme. */
export type MassnahmenVorgang = "lesen" | "anlegen";

/** Den Statuscode, den `request` in `api.ts` an einen Fehler hängt; ohne Antwort (Netz) `undefined`. */
function statusVon(fehler: unknown): number | undefined {
  if (typeof fehler !== "object" || fehler === null || !("status" in fehler)) return undefined;
  const status = (fehler as { status: unknown }).status;
  return typeof status === "number" ? status : undefined;
}

/**
 * Der Hinweis, wenn Lesen oder Anlegen scheitert. Er sagt, was zu tun ist, nicht nur, dass etwas
 * schiefging, und unterscheidet zwei Fälle:
 *
 * - **403, Person außerhalb der Berichtslinie.** Ein neuer Versuch hilft nicht, der Text rät auch nicht
 *   dazu. Er nennt den Grund und wohin man sich wendet, wenn die Zuordnung nicht stimmt.
 * - **Alles andere** (Serverfehler, Personio nicht konfiguriert oder nicht erreichbar, kein Netz): Das
 *   kann vorübergehend sein, also neuer Versuch, und wenn es bleibt, an den Administrator.
 *
 * Ein Fehler ohne Statuscode (kein Netz) oder etwas, das gar kein Fehlerobjekt ist, bekommt den
 * zweiten Text.
 */
export function massnahmenFehlerText(vorgang: MassnahmenVorgang, fehler: unknown): string {
  const ausserhalbDerLinie = statusVon(fehler) === 403;
  switch (vorgang) {
    case "lesen":
      return ausserhalbDerLinie
        ? "Die Maßnahmen dieses Mitarbeiters kannst du nicht sehen, weil er nicht in deiner Berichtslinie liegt. Stimmt das nicht, wende dich an einen Administrator."
        : "Die Maßnahmen konnten nicht geladen werden. Bitte öffne den Mitarbeiter erneut oder versuche es später noch einmal. Bleibt es dabei, wende dich an einen Administrator.";
    case "anlegen":
      return ausserhalbDerLinie
        ? "Für diesen Mitarbeiter kannst du keine Maßnahme anlegen, weil er nicht in deiner Berichtslinie liegt. Stimmt das nicht, wende dich an einen Administrator."
        : "Die Maßnahme konnte nicht gespeichert werden. Bitte versuche es noch einmal. Bleibt es dabei, wende dich an einen Administrator.";
  }
}
